import { createFeature, createSelector } from '@ngrx/store';
import { SELECT_ORDER_IS_VALIDATING, SELECT_ORDER_VALIDATION_METAFIELDS } from '@store/order';
import {
  PREPAYMENT_PLUGINS_USER_BALANCE_STORE_KEY,
  PREPAYMENT_PLUGINS_USER_BALANCE_STORE_REDUCER,
} from './prepayment-plugins-user-balance.reducer';
import { SELECT_SHOP_ACTIVE, SELECT_SHOP_ENTITY } from '@store/shop';
import { ShopTCPOSPlugin } from '@core/models/shop.model';

const { selectPrepaymentPluginsUserBalanceState } = createFeature({
  name: PREPAYMENT_PLUGINS_USER_BALANCE_STORE_KEY,
  reducer: PREPAYMENT_PLUGINS_USER_BALANCE_STORE_REDUCER,
});

export const SELECT_PREPAYMENT_STATE = selectPrepaymentPluginsUserBalanceState;

export const SELECT_PREPAYMENT_LOADING = (shopId: string) =>
  createSelector(selectPrepaymentPluginsUserBalanceState, state => {
    return state[shopId]?.isLoading ?? false;
  });

export const SELECT_PREPAYMENT_ORDER_LOADING = createSelector(
  SELECT_ORDER_IS_VALIDATING,
  isOrderValidating => isOrderValidating,
);

export const SELECT_PREPAYMENT_ORDER_LOADED = createSelector(
  SELECT_ORDER_IS_VALIDATING,
  isOrderValidating => !isOrderValidating,
);

export const SELECT_PREPAYMENT_LOADED = (shopId: string) =>
  createSelector(
    selectPrepaymentPluginsUserBalanceState,
    state => state[shopId]?.isLoaded ?? false,
  );

export const SELECT_BALANCE_BEFORE_ORDER_IN_CENTS = (shopId: string) =>
  createSelector(
    selectPrepaymentPluginsUserBalanceState,
    state => state[shopId]?.availableBalance ?? 0,
  );

export const SELECT_PREPAYMENT_ORDER_BALANCE_BEFORE_ORDER_IN_CENTS = createSelector(
  SELECT_ORDER_VALIDATION_METAFIELDS,
  metafields => {
    if (metafields) {
      const value = metafields?.find(mf => mf.handle === 'tcpos_user_balance_before_order')?.value;
      if (!value) {
        return null;
      }
      const intValue = parseInt(value, 10);
      if (isNaN(intValue)) {
        return null;
      }
      return intValue;
    }
    return null;
  },
);

export const SELECT_PREPAYMENT_USER_HAS_A_BALANCE_ACCOUNT = (shopId: string) =>
  createSelector(
    selectPrepaymentPluginsUserBalanceState,
    state => state[shopId]?.hasPrepaymentAccount ?? true,
  );

export const SELECT_PREPAYMENT_ORDER_USER_HAS_A_BALANCE_ACCOUNT = createSelector(
  SELECT_ORDER_VALIDATION_METAFIELDS,
  orderMetaFields => {
    const value = orderMetaFields?.find(mf => mf.handle.startsWith('tcpos_customer_id_shop_'));
    return !!value;
  },
);

export const SELECT_PREPAYMENT_ORDER_ACTIVE_SHOP_ID = createSelector(
  SELECT_ORDER_VALIDATION_METAFIELDS,
  orderMetaFields => {
    const value = orderMetaFields
      ?.find(mf => mf.handle.startsWith('tcpos_customer_id_shop_'))
      ?.handle.split('tcpos_customer_id_shop_')[1];
    return value;
  },
);

export const SELECT_PREPAYMENT_ORDER_BALANCE_AFTER_ORDER_IN_CENTS = createSelector(
  SELECT_ORDER_VALIDATION_METAFIELDS,
  metafields => {
    const value = metafields?.find(mf => mf.handle === 'tcpos_user_balance_after_order')?.value;
    if (!value) {
      return null;
    }
    const intValue = parseInt(value, 10);
    if (isNaN(intValue)) {
      return null;
    }
    return intValue;
  },
);

/**
 * ! Only set when the order is fetched
 */
export const SELECT_PREPAYMENT_ORDER_USER_CAN_USE_PREPAYMENT_FROM_ORDER = createSelector(
  SELECT_ORDER_VALIDATION_METAFIELDS,
  metafields => {
    const value = metafields?.some(mf => mf.handle.startsWith('tcpos'));
    return !!value;
  },
);

export const SELECT_PREPAYMENT_USER_CAN_USE_PREPAYMENT_FROM_ORDER =
  SELECT_PREPAYMENT_USER_HAS_A_BALANCE_ACCOUNT;

const SELECT_PREPAYMENT_SHOP_PLUGIN = createSelector(
  SELECT_SHOP_ACTIVE,
  shop => shop?.plugins.find(plugin => plugin.type === 'tcpos') as ShopTCPOSPlugin | undefined,
);

export const SELECT_PREPAYMENT_TOP_UP_PRESELECTED_AMOUNT_IN_CENTS = createSelector(
  SELECT_PREPAYMENT_SHOP_PLUGIN,
  prepaymentShopPlugin => {
    return (
      prepaymentShopPlugin?.top_up_available_amount.split(', ').map(data => parseInt(data, 10)) ??
      []
    );
  },
);

export const SELECT_PREPAYMENT_ACTIVE_PLUGIN_TYPE = createSelector(
  SELECT_PREPAYMENT_SHOP_PLUGIN,
  prepaymentShopPlugin => prepaymentShopPlugin?.type,
);

export const SELECT_PREPAYMENT_PLUGIN_TYPE_BY_SHOP = (shopId: string) =>
  createSelector(SELECT_SHOP_ENTITY(shopId), shop => {
    return shop?.plugins.find(plugin => plugin.type === 'tcpos')?.type as
      ShopTCPOSPlugin['type'] | undefined;
  });

export const SELECT_PREPAYMENT_TOP_UP_PRODUCT_ID = createSelector(
  SELECT_PREPAYMENT_SHOP_PLUGIN,
  prepaymentShopPlugin => {
    return prepaymentShopPlugin?.top_up_product_id;
  },
);

export const SELECT_PREPAYMENT_TOP_UP_AMOUNT_MIN_IN_CENTS = createSelector(
  SELECT_PREPAYMENT_SHOP_PLUGIN,
  prepaymentShopPlugin => {
    return prepaymentShopPlugin?.top_up_amount_min;
  },
);

export const SELECT_PREPAYMENT_TOP_UP_AMOUNT_MAX_IN_CENTS = createSelector(
  SELECT_PREPAYMENT_SHOP_PLUGIN,
  prepaymentShopPlugin => {
    return prepaymentShopPlugin?.top_up_amount_max;
  },
);

export const SELECT_CAN_DISPLAY_PREPAYMENT_ACCOUNT = createSelector(
  SELECT_PREPAYMENT_USER_HAS_A_BALANCE_ACCOUNT,
  userHasABalanceAccount => userHasABalanceAccount,
);
